// UserReportsController.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUserReports,
  fetchTargetAchievementReport, fetchCollectionsReport, fetchPendingDispatchReport, fetchOutstandingReport, fetchSalesReport, fetchStockLevelReport, fetchUserParties

} from "Service/ApiService";

const UserReportsController = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(today);
  const [reportData, setReportData] = useState(null);

  // Filters
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState({});

  const [categories, setCategories] = useState([]);
  const [selectedCategorie, setSelectedCategorie] = useState({});

  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState({});

  // Reports Data
  const [targetAchievementReportData, setTargetAchievementReportData] = useState({});
  const [collectionsReportData, setCollectionsReportData] = useState({});
  const [pendingDispatchReportData, setPendingDispatchReportData] = useState({});
  const [outstandingReportData, setOutstandingReportData] = useState({});
  const [salesReportData, setSalesReportData] = useState({});
  const [stockLevelReportData, setStockLevelReportData] = useState({});

  const [salesView, setSalesView] = useState("category");

  // defensive dummy data (matches prototype structure)
  const dummy = {
    sales: {
      labels: ["Apr 25", "May 25", "Jun 25", "Jul 25", "Aug 25", "Sep 25", "Oct 25", "Nov 25", "Jan 26", "Feb 26", "Mar 26"],
      series: {
        Suiting: [120540, 149978, 149231, 99941, 63410, 69085, 133963, 84409, 177047, 128560, 147036],
        Shirting: [106267, 145716, 135161, 102717, 92925, 83867, 114290, 169409, 139103, 73489, 128309],
        Others: [85144, 65990, 66793, 179971, 149418, 124870, 104151, 118490, 148385, 117015, 77334]
      }
    },
    salesDivision: {
      labels: ["Apr 25", "May 25", "Jun 25", "Jul 25", "Aug 25", "Sep 25", "Oct 25", "Nov 25", "Jan 26", "Feb 26", "Mar 26"],
      series: {
        O2: [120540, 149978, 149231, 99941, 63410, 69085, 133963, 84409, 177047, 128560, 147036],
        O3: [106267, 145716, 135161, 102717, 92925, 83867, 114290, 169409, 139103, 73489, 128309],
        Others: [85144, 65990, 66793, 179971, 149418, 124870, 104151, 118490, 148385, 117015, 77334]
      }
    },
    stock: { cats: ["Cotton", "Linen", "Denim", "Silk", "Viscose", "Rayon", "Polyester"], vals: [1200, 850, 600, 1400, 900, 700, 1000] },
    outstanding: {
      parties: ["ABC Traders", "XYZ Corporation", "PQR Wholesalers"],
      agingBuckets: ["0-30", "31-60", "61-90", "91-120", "121-150", "151-365", "365+"],
      values: {
        "ABC Traders": [95613, 166107, 90868, 122044, 95423, 141384, 30678],
        "XYZ Corporation": [152242, 74195, 109637, 192693, 151567, 117457, 130164],
        "PQR Wholesalers": [101749, 57615, 192297, 22310, 166087, 23462, 163448]
      }
    },
    pendingDispatch: {
      labels: ["Apr 25", "May 25", "Jun 25", "Jul 25", "Aug 25", "Sep 25", "Oct 25", "Nov 25", "Jan 26", "Feb 26", "Mar 26"],
      pending: [80, 95, 120, 110, 88, 76, 130, 140, 112, 98, 105],
      dispatch: [60, 85, 100, 90, 78, 70, 110, 120, 95, 88, 100]
    },
    target: {
      users: {
        SM1: { target: [120000, 130000, 125000, 110000, 115000, 100000, 140000, 130000, 150000, 135000, 145000], achieve: [110000, 125000, 120000, 115000, 108000, 95000, 130000, 128000, 148000, 130000, 150000] },
        SM2: { target: [100000, 110000, 105000, 98000, 102000, 95000, 120000, 115000, 125000, 118000, 130000], achieve: [98000, 108000, 100000, 92000, 101000, 94000, 118000, 112000, 123000, 116000, 129000] }
      }
    },
    collections: {
      "ABC Traders": [300000, 250000, 280000, 310000, 290000, 270000, 300000, 320000, 310000, 330000, 340000],
      "XYZ Corporation": [200000, 180000, 210000, 220000, 205000, 195000, 200000, 210000, 220000, 230000, 240000],
      "PQR Wholesalers": [150000, 160000, 155000, 145000, 148000, 150000, 152000, 151000, 149000, 150000, 151000]
    },
    summary: {
      totalBeats: 12,
      totalCustomers: 840,
      totalOrders: 4520,
      pendingOrders: 120,
      completedOrders: 4400,
      totalSales: 12543000,
      todayVisits: 18
    }
  };

  useEffect(() => {
    getUserParties();
  }, []);

  const load = async (date = selectedDate) => {
    setLoading(true);
    try {
      if (!user?.userCode) {
        // fallback to dummy
        setReportData(dummy);
        setLoading(false);
        return;
      }

      const res = await fetchUserReports(user.userCode, date);
      if (res && res.success && res.data) {
        // expect API to return same shaped data; otherwise adapt here
        setReportData(res.data);
      } else {
        // fallback to dummy if API returns no data
        setReportData(dummy);
      }
    } catch (err) {
      console.error("UserReports load error:", err);
      setReportData(dummy);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    getTargetAchievementReport();
    getCollectionsReport();
    getPendingDispatchReport();
    getOutstandingReport();
    getSalesReport();
    getStockLevelReport();


    // Set Dummy Filters Data
    setReportTypes([
      { value: "monthly", label: "Monthly Report" },
      { value: "daily", label: "Daily Report" }
    ]);

    setCategories([
      { value: "suiting", label: "Suiting" },
      { value: "shirting", label: "Shirting" },
      { value: "others", label: "Others" }
    ]);
  }, []);

  useEffect(() => {
    getTargetAchievementReport();
    getCollectionsReport();
    getPendingDispatchReport();
    getOutstandingReport();
    getSalesReport();
    getStockLevelReport();
  }, [selectedCategorie, selectedParty, selectedReportType]);

  // Set defalt selected filter values
  if (reportTypes.length > 0 && !selectedReportType.value) {
    setSelectedReportType(reportTypes[0]);
  }
  if (categories.length > 0 && !selectedCategorie.value) {
    setSelectedCategorie(categories[0]);
  }

  const goTo = (route, extra = {}) => {
    navigate(route, { state: { selectedDate, ...extra } });
  };

  // Get target vs achievement report data
  const getTargetAchievementReport = async () => {
    setLoading(true);
    try {
      const res = await fetchTargetAchievementReport(selectedParty.value || user.userCode);
      // console.log('res target--->>',res.data);
      //const res = await fetchTargetAchievementReport(1000);
      if (res && res.success) {
        setTargetAchievementReportData(res.data);
      } else {
        setTargetAchievementReportData({});
      }
    } catch (err) {
      console.error("User Target vs Acheievement Reports load error:", err);
    } finally {
      setLoading(false);
    }
  }

  // Get collections report data
  const getCollectionsReport = async () => {
    setLoading(true);
    try {
      const res = await fetchCollectionsReport(selectedParty.value || user.userCode);
      // const res = await fetchCollectionsReport(1500);
      if (res && res.success) {
        setCollectionsReportData(res.data);
      } else {
        setCollectionsReportData({});
      }
    } catch (err) {
      console.error("User Collections Reports load error:", err);
    } finally {
      setLoading(false);
    }
  }

  // Get pending vs dispatch report data
  const getPendingDispatchReport = async () => {
    setLoading(true);
    try {
      const res = await fetchPendingDispatchReport(selectedParty.value || user.userCode);
      // const res = await fetchPendingDispatchReport(1000);
      if (res && res.success) {
        setPendingDispatchReportData(res.data);
      } else {
        setPendingDispatchReportData({});
      }
    } catch (err) {
      console.error("User Pending vs Dispatch Reports load error:", err);
    } finally {
      setLoading(false);
    }
  }

  // Get outstanding report data
  const getOutstandingReport = async () => {
    setLoading(true);
    try {
      const res = await fetchOutstandingReport(selectedParty.value || user.userCode);
      // const res = await fetchOutstandingReport(1000);
      if (res && res.success) {
        setOutstandingReportData(res.data);
      } else {
        setOutstandingReportData({});
      }
    } catch (err) {
      console.error("User outstanding Reports load error:", err);
    } finally {
      setLoading(false);
    }
  }

  // Get Sales report data
  const getSalesReport = async () => {
    setLoading(true);
    try {
      const res = await fetchSalesReport(selectedParty.value || user.userCode);
      //const res = await fetchSalesReport(1000);
      if (res && res.success) {
        setSalesReportData(res.data);
      } else {
        setSalesReportData({});
      }
    } catch (err) {
      console.error("User Sales Reports load error:", err);
    } finally {
      setLoading(false);
    }
  }

  // Get stock level report data
  const getStockLevelReport = async () => {
    setLoading(true);
    try {
      const res = await fetchStockLevelReport(selectedParty.value || user.userCode);
      //const res = await fetchSalesReport(1000);
      // console.log('stock response',res.data);
      if (res && res.success) {
        setStockLevelReportData(res.data);
      } else {
        setStockLevelReportData({});
      }
    } catch (err) {
      console.error("Stock level Reports load error:", err);
    } finally {
      setLoading(false);
    }
  }

  const getUserParties = async () => {
    try {
      const res = await fetchUserParties(user.userCode);
      if (res && res.success) {
        const dropdownData = res.partyCodes.map((item) => ({
          value: item.partyCode,
          label: `${item.partyName || "Unknown"} - ${item.partyCode}`,
        }));
        setParties(dropdownData);
        setSelectedParty(dropdownData[0] || null);
      } else {
        setParties([]);
        setSelectedParty(null);
      }
    } catch (err) {
      console.error("User Parties load error:", err);
      setParties([]);
      setSelectedParty(null);
    }
  };

  return {
    loading,

    selectedDate,
    setSelectedDate,
    reportData,
    goTo,
    reload: () => load(selectedDate),

    // Filters
    reportTypes,
    categories,
    parties,

    selectedReportType,
    selectedCategorie,
    selectedParty,

    setSelectedReportType,
    setSelectedCategorie,
    setSelectedParty,

    // Reports Data
    targetAchievementReportData,
    collectionsReportData,
    pendingDispatchReportData,
    outstandingReportData,
    salesReportData,
    stockLevelReportData,

    salesView,
    setSalesView,
  };
};

export default UserReportsController;
